import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { ChevronDown, ChevronRight, ShieldCheck, Info, Sparkles } from "lucide-react";

const ROLE_LABEL = { manager: "Manager", supervisor: "Supervisor", staff: "Staff" };

export default function AccessMatrix({ meRole, onSaved }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const isOwner = meRole === "owner";

  const load = async () => {
    try {
      const { data: d } = await api.get("/tenant/access-matrix");
      setData(d);
      setDraft(JSON.parse(JSON.stringify(d.matrix)));
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const {
    registry, ceiling, recommended, minimum_module,
    allow_manager_edit_matrix, manager_editable_roles,
    last_updated_by, last_updated_at,
  } = data;

  const editableRows = isOwner ? ["manager", "supervisor", "staff"]
    : (allow_manager_edit_matrix ? manager_editable_roles : []);

  const canToggle = (role, key) => {
    if (!editableRows.includes(role)) return false;
    if (key === minimum_module) return false;  // always ticked
    return (ceiling[role] || []).includes(key);
  };

  const toggle = (role, key) => {
    if (!canToggle(role, key)) return;
    const set = new Set(draft[role] || []);
    if (set.has(key)) set.delete(key); else set.add(key);
    setDraft({ ...draft, [role]: Array.from(set) });
  };

  const applyRecommended = (role) => {
    if (!editableRows.includes(role)) return;
    setDraft({ ...draft, [role]: [...(recommended[role] || [])] });
  };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const body = {};
      editableRows.forEach((r) => { body[r] = draft[r] || []; });
      const { data: d } = await api.put("/tenant/access-matrix", body);
      setData((prev) => ({
        ...prev,
        matrix: d.matrix,
        is_customized: true,
        last_updated_by: prev.last_updated_by,  // server will set on next load
      }));
      setDraft(JSON.parse(JSON.stringify(d.matrix)));
      setMsg("Access updated. Team members see the new access on their next page load.");
      onSaved?.();
      // Re-fetch to pull fresh last_updated_by/at.
      load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  // Optimistic delegation toggle — update UI first, revert on API error.
  const toggleDelegation = async (e) => {
    if (!isOwner || busy) return;
    const newVal = e.target.checked;
    setErr(""); setMsg("");
    setData((prev) => ({ ...prev, allow_manager_edit_matrix: newVal }));
    setBusy(true);
    try {
      await api.put("/tenant/access-matrix/settings", { allow_manager_edit_matrix: newVal });
      setMsg(newVal
        ? "Managers can now edit Supervisor and Staff access."
        : "Managers can no longer edit access.");
    } catch (ex) {
      setData((prev) => ({ ...prev, allow_manager_edit_matrix: !newVal }));  // revert
      setErr(formatErr(ex.response?.data?.detail) || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const isDirty = JSON.stringify(draft) !== JSON.stringify(data.matrix);

  return (
    <div className="vb-card mb-6" data-testid="access-matrix">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left"
        data-testid="access-matrix-toggle"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck size={18} style={{ color: "var(--vb-text-2)" }} />
          <div>
            <div className="vb-heading text-base" style={{ color: "var(--vb-text-2)" }}>Role access</div>
            <div className="text-xs opacity-60">Choose which modules each App Role can see. Owner always has full access.</div>
          </div>
        </div>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>

      {open && (
        <div className="p-4 pt-0">
          <div className="mb-3 p-3 rounded-lg text-xs flex items-start gap-2" style={{ background: "var(--vb-bg-2)" }}>
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>Owner</strong> always sees every module. For Manager, Supervisor, and Staff you can tick any module below.
              <strong> Dashboard</strong> is locked on for every role. Scroll right to see more modules or to reach the recommended preset.
              Page access below does <em>not</em> override role-level write permissions (e.g. Staff can never create equipment even if the module is on).
            </span>
          </div>

          <div className="vb-access-scroll" data-testid="access-matrix-scroll">
            <table className="vb-table vb-access-table" data-testid="access-matrix-table">
              <thead>
                <tr>
                  <th className="vb-access-sticky">App Role</th>
                  {registry.map((m) => (
                    <th key={m.key} title={m.description} className="text-center whitespace-nowrap">{m.label}</th>
                  ))}
                  <th className="text-center whitespace-nowrap">Preset</th>
                </tr>
              </thead>
              <tbody>
                {["manager", "supervisor", "staff"].map((role) => (
                  <tr key={role} data-testid={`access-row-${role}`}>
                    <td className="vb-access-sticky"><strong>{ROLE_LABEL[role]}</strong></td>
                    {registry.map((m) => {
                      const checked = (draft?.[role] || []).includes(m.key);
                      const editable = canToggle(role, m.key);
                      const isRec = (recommended[role] || []).includes(m.key);
                      const isMin = m.key === minimum_module;
                      return (
                        <td key={m.key} className="text-center">
                          <input
                            type="checkbox"
                            checked={checked || isMin}
                            onChange={() => toggle(role, m.key)}
                            disabled={!editable}
                            title={isMin ? "Required — always visible" : (isRec ? "Recommended default" : "")}
                            data-testid={`access-cell-${role}-${m.key}`}
                          />
                        </td>
                      );
                    })}
                    <td className="text-center">
                      <button
                        type="button"
                        className="vb-btn ghost text-xs"
                        onClick={() => applyRecommended(role)}
                        disabled={!editableRows.includes(role)}
                        title="Apply platform-recommended default for this role"
                        data-testid={`access-preset-${role}`}
                      >
                        <Sparkles size={12} /> Recommended
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isOwner && (
            <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" data-testid="access-delegation-toggle">
              <input
                type="checkbox"
                checked={!!allow_manager_edit_matrix}
                onChange={toggleDelegation}
                disabled={busy}
                data-testid="access-delegation-input"
              />
              <span>Let <strong>Managers</strong> edit Supervisor and Staff access (they can never change their own row).</span>
            </label>
          )}

          {last_updated_by && (
            <div className="text-xs opacity-60 mt-3" data-testid="access-matrix-meta">
              Last updated by <strong>{last_updated_by}</strong> · {new Date(last_updated_at).toLocaleString()}
            </div>
          )}

          {err && <div className="vb-pill err mt-3">{err}</div>}
          {msg && <div className="vb-pill ok mt-3">{msg}</div>}

          {editableRows.length > 0 && (
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button" className="vb-btn ghost"
                onClick={() => setDraft(JSON.parse(JSON.stringify(data.matrix)))}
                disabled={busy || !isDirty}
                data-testid="access-matrix-reset"
              >Discard changes</button>
              <button
                type="button" className="vb-btn"
                onClick={save}
                disabled={busy || !isDirty}
                data-testid="access-matrix-save"
              >{busy ? "Saving…" : "Save access"}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
